import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col"
}
const _hoisted_2 = { class: "flex flex-col gap-3 px-4 pt-3 pb-1 max-h-72 overflow-y-auto" }
const _hoisted_3 = ["data-cy"]
const _hoisted_4 = { class: "flex items-center justify-between mb-1" }
const _hoisted_5 = { class: "text-xs font-medium text-gray-600" }
const _hoisted_6 = { class: "flex justify-end gap-2 px-4 py-2" }
const _hoisted_7 = {
  key: 0,
  class: "flex flex-wrap items-center gap-1 mt-2 max-w-56"
}

import { computed, ref } from 'vue';
import type { PropType } from 'vue';
import LlMenu from '@/components/LlMenu.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlRadioGroup from '@/components/LlRadioGroup.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { NOT_ASSIGNED_OPTION } from '@/constants/customFieldFilterConstants';
import type {
  CustomFieldFilterSelections,
  FilterableCustomField,
} from '@/models/customFieldFilterTypes';

// Presentational: the page loads its own fields (matter or client) and owns the
// applied selections, so this stays free of any feature's store.
interface AppliedChip {
  fieldId: string;
  text: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomFieldsFilter',
  props: {
  fields: { type: Array as PropType<FilterableCustomField[]>, required: true },
  modelValue: { type: Object as PropType<CustomFieldFilterSelections>, required: true },
},
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Picks made while the popover is open; only Apply commits them.
const pending = ref<CustomFieldFilterSelections>({});

const startEditing = () => {
  pending.value = { ...props.modelValue };
};

const setPending = (fieldId: string, optionId: string) => {
  pending.value = { ...pending.value, [fieldId]: optionId };
};

const withoutField = (selections: CustomFieldFilterSelections, fieldId: string) => {
  const next = { ...selections };
  delete next[fieldId];
  return next;
};

const clearField = (fieldId: string) => {
  pending.value = withoutField(pending.value, fieldId);
};

const applyPending = (close: () => void) => {
  emit('update:modelValue', { ...pending.value });
  close();
};

const optionsFor = (field: FilterableCustomField) => [
  { label: 'Not assigned', value: NOT_ASSIGNED_OPTION },
  ...(field.enumOptions ?? []).map((opt) => ({ label: opt.value || opt.label || '', value: opt.id })),
];

const appliedChips = computed<AppliedChip[]>(() =>
  Object.entries(props.modelValue)
    .filter(([, value]) => value)
    .map(([fieldId, value]): AppliedChip | null => {
      const field = props.fields.find((f) => f.id === fieldId);
      if (!field) return null;
      const option = (field.enumOptions ?? []).find((o) => o.id === value);
      const label = value === NOT_ASSIGNED_OPTION ? 'Not assigned' : option?.value || option?.label || value;
      return { fieldId, text: `${field.name}: ${label}` };
    })
    .filter((chip): chip is AppliedChip => chip !== null),
);

const activeCount = computed(() => appliedChips.value.length);

const removeField = (fieldId: string) => {
  emit('update:modelValue', withoutField(props.modelValue, fieldId));
};

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card = _resolveComponent("v-card")!

  return (__props.fields.length)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _cache[7] || (_cache[7] = _createElementVNode("div", {
          class: "h-7 mb-[5px]",
          "aria-hidden": "true"
        }, null, -1)),
        _createVNode(LlMenu, {
          "close-on-content-click": false,
          location: "bottom start",
          "min-width": 260,
          "max-width": 300,
          onMenuOpen: startEditing
        }, {
          activator: _withCtx(({ props: menuProps }) => [
            _createVNode(LlSecondaryButton, _mergeProps(menuProps, {
              variant: "outlined",
              density: "comfortable",
              class: "text-none !bg-[rgb(var(--v-theme-surface))]",
              "data-cy": "custom-fields-filter-button"
            }), {
              default: _withCtx(() => [
                _createVNode(LlIcon, {
                  icon: "mdi-filter-variant",
                  size: "18",
                  class: "mr-2"
                }),
                _cache[2] || (_cache[2] = _createTextVNode(" Custom Fields ", -1)),
                (activeCount.value > 0)
                  ? (_openBlock(), _createBlock(_component_v_chip, {
                      key: 0,
                      size: "x-small",
                      color: "primary",
                      variant: "flat",
                      class: "ml-2"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(activeCount.value), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 16)
          ]),
          content: _withCtx(({ close }) => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.fields, (field) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: field.id,
                      "data-cy": `custom-field-filter-${field.id}`
                    }, [
                      _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("span", _hoisted_5, _toDisplayString(field.name), 1),
                        (pending.value[field.id])
                          ? (_openBlock(), _createBlock(LlSecondaryButton, {
                              key: 0,
                              variant: "text",
                              size: "x-small",
                              class: "text-none",
                              onClick: ($event: any) => (clearField(field.id))
                            }, {
                              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                _createTextVNode(" Clear ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["onClick"]))
                          : _createCommentVNode("", true)
                      ]),
                      _createVNode(LlRadioGroup, {
                        "model-value": pending.value[field.id],
                        options: optionsFor(field),
                        inline: false,
                        "onUpdate:modelValue": ($event: any) => (setPending(field.id, $event))
                      }, null, 8, ["model-value", "options", "onUpdate:modelValue"])
                    ], 8, _hoisted_3))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(LlSecondaryButton, {
                    variant: "text",
                    size: "small",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (pending.value = {}))
                  }, {
                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode(" Clear all ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(LlSecondaryButton, {
                    variant: "flat",
                    size: "small",
                    color: "primary",
                    "data-cy": "custom-fields-filter-apply",
                    onClick: ($event: any) => (applyPending(close))
                  }, {
                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                      _createTextVNode(" Apply ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["onClick"])
                ])
              ]),
              _: 2
            }, 1024)
          ]),
          _: 1
        }),
        (appliedChips.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(appliedChips.value, (chip) => {
                return (_openBlock(), _createBlock(_component_v_chip, {
                  key: chip.fieldId,
                  size: "small",
                  color: "primary",
                  variant: "text",
                  closable: "",
                  class: "!bg-[rgb(var(--v-theme-surface))] border border-outline-variant border-opacity-100",
                  "onClick:close": ($event: any) => (removeField(chip.fieldId))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(chip.text), 1)
                  ]),
                  _: 2
                }, 1032, ["onClick:close"]))
              }), 128)),
              _createVNode(LlSecondaryButton, {
                variant: "text",
                size: "x-small",
                class: "text-none underline",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('update:modelValue', {})))
              }, {
                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode(" Clear all ", -1)
                ]))]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})